import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import { buildApiUrl } from '../config/environment';

const ChargeModal = ({ show, onClose, charge, tenancyLedgerId, onSave }) => {
  const { agencyCurrency, user, agencyCountry, agencyId } = useContext(AuthContext);
  const currencySymbol = getCurrencySymbol(agencyCurrency);

  const formatDateWithContext = (dateString) =>
    formatDate(dateString, { userPreference: user?.dateFormatPreference, agencyCountry });

  const [formData, setFormData] = useState({
    amount: '',
    category_id: '',
    note: '',
    incurred_at: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Dynamic categories from backend
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Fetch charge categories from API
  useEffect(() => {
    if (!show || !agencyId) return;

    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const token = sessionStorage.getItem('access_token') ||
                     sessionStorage.getItem('token') ||
                     localStorage.getItem('access_token') ||
                     localStorage.getItem('token');

        const response = await fetch(
          buildApiUrl(`/agencies/${agencyId}/charge-categories`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setCategories(data || []);
        } else {
          console.error('[ChargeModal] Failed to fetch charge categories:', response.status);
          setCategories([]);
        }
      } catch (error) {
        console.error('[ChargeModal] Error fetching charge categories:', error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [show, agencyId]);

  useEffect(() => {
    if (charge) {
      // Resolve category_id: prefer the explicit field, fall back to nested object
      const categoryId = charge.category_id || charge.CategoryID || (charge.category?.id) || '';
      setFormData({
        amount: charge.amount?.amount ? (charge.amount.amount / 100) : (charge.amount || ''),
        category_id: categoryId,
        note: charge.note || charge.description || '',
        incurred_at: charge.incurred_at ? charge.incurred_at.split('T')[0] :
                     (charge.charge_date ? charge.charge_date.split('T')[0] :
                     new Date().toISOString().split('T')[0])
      });
    } else {
      setFormData({
        amount: '',
        category_id: '',
        note: '',
        incurred_at: new Date().toISOString().split('T')[0]
      });
    }
    setErrors({});
  }, [charge, show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be a positive number.';
    }
    if (!formData.category_id) {
      newErrors.category_id = 'Category is required.';
    }
    if (!formData.incurred_at) {
      newErrors.incurred_at = 'Charge date is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createCharge = async (chargeData) => {
    try {
      const token = sessionStorage.getItem('access_token') ||
                   sessionStorage.getItem('token') ||
                   localStorage.getItem('access_token') ||
                   localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Structure the payload with category_id (integer FK)
      const payload = {
        amount: parseFloat(chargeData.amount),
        category_id: parseInt(chargeData.category_id, 10),
        incurred_at: new Date(chargeData.incurred_at).toISOString(),
        note: chargeData.note || ""
      };

      console.log('[ChargeModal] Creating charge with payload:', payload);

      const response = await fetch(
        buildApiUrl(`/tenancy-ledgers/${tenancyLedgerId}/charges`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to create charge' }));
        throw new Error(error.message || `HTTP ${response.status}: Failed to create charge`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating charge:', error);
      throw error;
    }
  };

  const updateCharge = async (chargeData) => {
    try {
      const token = sessionStorage.getItem('access_token') ||
                   sessionStorage.getItem('token') ||
                   localStorage.getItem('access_token') ||
                   localStorage.getItem('token');

      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Structure the payload with category_id (integer FK)
      const payload = {
        amount: parseFloat(chargeData.amount),
        category_id: parseInt(chargeData.category_id, 10),
        incurred_at: new Date(chargeData.incurred_at).toISOString(),
        note: chargeData.note || undefined
      };

      console.log('[ChargeModal] Updating charge with payload:', payload);

      const response = await fetch(
        buildApiUrl(`/tenancy-ledgers/charges/${charge.id}`),
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update charge' }));
        throw new Error(error.message || `HTTP ${response.status}: Failed to update charge`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating charge:', error);
      throw error;
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const savedCharge = charge
        ? await updateCharge(formData)
        : await createCharge(formData);
      onSave(savedCharge);
      onClose();
    } catch (error) {
      alert(`Failed to ${charge ? 'update' : 'create'} charge: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      amount: '',
      category_id: '',
      note: '',
      incurred_at: new Date().toISOString().split('T')[0]
    });
    onClose();
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.6)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-warning text-dark">
            <div>
              <h5 className="modal-title mb-1">
                <i className="bi bi-receipt me-2"></i>
                {charge ? 'Edit Charge' : 'Add New Charge'}
              </h5>
              <small className="opacity-75">
                {charge ? 'Update charge details' : 'Create a new charge record'}
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            <div className="row g-4">
              {/* Amount */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Amount <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">{currencySymbol}</span>
                  <input
                    type="number"
                    className={`form-control form-control-lg ${errors.amount ? 'is-invalid' : ''}`}
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    required
                  />
                  {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                </div>
              </div>

              {/* Category */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Category <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select form-select-lg ${errors.category_id ? 'is-invalid' : ''}`}
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  required
                  disabled={loadingCategories}
                >
                  <option value="">
                    {loadingCategories ? 'Loading categories...' : 'Select category...'}
                  </option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.display_name}
                    </option>
                  ))}
                </select>
                {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
              </div>

              {/* Date */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Charge Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${errors.incurred_at ? 'is-invalid' : ''}`}
                  name="incurred_at"
                  value={formData.incurred_at}
                  onChange={handleInputChange}
                  onKeyDown={(e) => e.preventDefault()}
                  required
                />
                {formData.incurred_at && (
                  <small className="form-text fw-semibold text-primary">
                    {formatDateWithContext(formData.incurred_at)}
                  </small>
                )}
                {errors.incurred_at && <div className="invalid-feedback">{errors.incurred_at}</div>}
              </div>

              {/* Note */}
              <div className="col-12">
                <label className="form-label fw-semibold">Note</label>
                <textarea
                  className="form-control"
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Additional details about this charge..."
                />
              </div>
            </div>
          </div>

          <div className="modal-footer border-top-0 bg-light">
            <button className="btn btn-outline-secondary" onClick={handleCancel} disabled={saving}>
              <i className="bi bi-x-lg me-2"></i>
              Cancel
            </button>
            <button
              className="btn btn-warning"
              onClick={handleSave}
              disabled={saving || !formData.amount || !formData.category_id}
            >
              {saving ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Saving...</span>
                  </div>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2"></i>
                  {charge ? 'Update' : 'Create'} Charge
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargeModal;
